import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { bestSellersApi } from '../lib/api';

const BestSellersSection = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    bestSellersApi
      .list()
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector('[data-bestseller-card]');
    const delta = card ? card.getBoundingClientRect().width + 20 : 320;
    el.scrollBy({ left: dir * delta, behavior: 'smooth' });
  };

  if (loaded && items.length === 0) return null;

  return (
    <section className="px-6 lg:px-10 py-24" data-testid="best-sellers-section">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
        <div>
          <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block">
            VELOUVA SIGNATURES
          </div>
          <h2 className="font-serif text-[44px] lg:text-[56px] leading-[1.05] mt-6">
            Best <span className="italic">sellers.</span>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Previous"
            data-testid="best-sellers-prev"
            className="w-10 h-10 border border-[#1a1410] flex items-center justify-center hover:bg-[#1a1410] hover:text-[#f5f1e6] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.6} />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Next"
            data-testid="best-sellers-next"
            className="w-10 h-10 border border-[#1a1410] flex items-center justify-center hover:bg-[#1a1410] hover:text-[#f5f1e6] transition-colors"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.6} />
          </button>
          <Link to="/shop" className="ml-4 text-[11px] tracking-[0.32em] border-b border-[#1a1410] pb-1">
            VIEW ALL
          </Link>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-6 lg:-mx-10 px-6 lg:px-10 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none' }}
      >
        {items.map((p) => (
          <Link
            key={p.id}
            to={`/product/${p.id}`}
            data-bestseller-card
            data-testid={`best-seller-card-${p.id}`}
            className="group snap-start shrink-0 w-[70%] sm:w-[45%] md:w-[32%] lg:w-[24%]"
          >
            <div className="overflow-hidden bg-[#e9e2d3] aspect-[3/4]">
              <img
                src={p.image}
                alt={p.name}
                className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                loading="lazy"
              />
            </div>
            <div className="mt-5 flex items-start justify-between gap-3">
              <div>
                <div className="font-serif text-[22px] leading-tight">{p.name}</div>
                <div className="text-xs tracking-[0.18em] text-[#7a6a5a] mt-1 uppercase">{p.shade}</div>
              </div>
              <div className="font-medium text-[16px] whitespace-nowrap tabular-nums">Rs. {p.price?.toFixed?.(2) ?? p.price}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default BestSellersSection;
